"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.eventsEvalFilterColumns = exports.experimentEvalFilterColumns = exports.observationEvalFilterColumns = exports.observationEvalVariableColumns = exports.experimentTargetEvalVariableColumns = exports.eventTargetEvalVariableColumns = exports.CODE_EVAL_TEMPLATE_VARIABLES = exports.toolCallForEvalSchema = exports.observationForEvalSchema = void 0;
exports.zipToolCallsFromRecord = zipToolCallsFromRecord;
exports.zipObservationToolCalls = zipObservationToolCalls;
exports.convertEventRecordToObservationForEval = convertEventRecordToObservationForEval;
exports.observationEvalFilterColsWithOptions = observationEvalFilterColsWithOptions;
exports.experimentEvalFilterColsWithOptions = experimentEvalFilterColsWithOptions;
exports.mapEventEvalFilterColumnIdToField = mapEventEvalFilterColumnIdToField;
const zod_1 = require("zod");
const types_1 = require("../../server/ingestion/types");
const domain_1 = require("../../domain");
const metadata_conversion_1 = require("../../server/utils/metadata_conversion");
const typeHelpers_1 = require("../../tableDefinitions/typeHelpers");
const json_1 = require("../../utils/json");
const flexibleUsageCostSchema = zod_1.z.record(zod_1.z.string(), zod_1.z.coerce.number().nullable());
exports.observationForEvalSchema = zod_1.z.object({
    // Identifiers
    span_id: zod_1.z.string(),
    trace_id: zod_1.z.string(),
    project_id: zod_1.z.string(),
    parent_span_id: zod_1.z.string().nullish(),
    // Core properties
    type: zod_1.z.string(),
    name: zod_1.z.string(),
    environment: zod_1.z.string().default(types_1.DEFAULT_TRACE_ENVIRONMENT),
    version: zod_1.z.string().nullish(),
    level: zod_1.z.string().default(domain_1.ObservationLevel.DEFAULT),
    status_message: zod_1.z.string().nullish(),
    // Trace-level properties
    trace_name: zod_1.z.string().nullish(),
    user_id: zod_1.z.string().nullish(),
    session_id: zod_1.z.string().nullish(),
    tags: zod_1.z.array(zod_1.z.string()).default([]),
    release: zod_1.z.string().nullish(),
    // Model
    provided_model_name: zod_1.z.string().nullish(),
    model_parameters: zod_1.z.unknown().nullish(),
    // Prompt
    prompt_id: zod_1.z.string().nullish(),
    prompt_name: zod_1.z.string().nullish(),
    // Accepts string, number, or any other type from ingestion
    prompt_version: zod_1.z.union([zod_1.z.string().nullish(), zod_1.z.number().nullish()]),
    // Usage & Cost - accepts number values directly from ingestion
    provided_usage_details: flexibleUsageCostSchema,
    provided_cost_details: flexibleUsageCostSchema,
    usage_details: flexibleUsageCostSchema,
    cost_details: flexibleUsageCostSchema,
    // Tool calls
    tool_definitions: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).default({}),
    tool_calls: zod_1.z.array(zod_1.z.unknown()).default([]),
    tool_call_names: zod_1.z.array(zod_1.z.string()).default([]),
    tool_call_count: zod_1.z.number().default(0),
    // Experiment
    experiment_id: zod_1.z.string().nullish(),
    experiment_name: zod_1.z.string().nullish(),
    experiment_description: zod_1.z.string().nullish(),
    experiment_dataset_id: zod_1.z.string().nullish(),
    experiment_item_id: zod_1.z.string().nullish(),
    experiment_item_expected_output: zod_1.z.string().nullish(),
    experiment_item_metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullish(),
    experiment_item_root_span_id: zod_1.z.string().nullish(),
    // Data - accepts any type (string, array, object) from different OTEL SDKs
    input: zod_1.z.unknown().nullish(),
    output: zod_1.z.unknown().nullish(),
    metadata: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullish(),
});
/**
 * Self-contained tool call shape handed to evaluators. Rebuilt from the
 * ClickHouse storage layout, which keeps names in a parallel array
 * (`tool_call_names`) so ClickHouse can filter without JSON parsing.
 */
exports.toolCallForEvalSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    arguments: zod_1.z.unknown(),
    type: zod_1.z.string(),
    index: zod_1.z.number(),
});
/**
 * zipObservationToolCalls for camelCase records with loosely typed arrays
 * (tRPC/domain shapes: events batchIO, legacy observations.byId). Malformed
 * names map to "" instead of being dropped — filtering would shift the zip
 * and misattribute every later call's name.
 */
function zipToolCallsFromRecord(record) {
    const { toolCalls, toolCallNames } = record;
    return zipObservationToolCalls({
        tool_calls: Array.isArray(toolCalls) ? toolCalls : [],
        tool_call_names: Array.isArray(toolCallNames)
            ? toolCallNames.map((name) => (typeof name === "string" ? name : ""))
            : [],
    });
}
/**
 * Zips the parallel arrays back into named tool call objects.
 * `tool_call_names` is authoritative for count and order: ingestion writes
 * both arrays in lockstep (`convertCallsToArrays`), and stored entries carry
 * no name. `arguments` arrives double-encoded (a JSON string inside the entry
 * JSON) and is parsed to an object; unparsable values stay raw strings.
 */
function zipObservationToolCalls(observation) {
    return observation.tool_call_names.map((name, i) => {
        const parsed = (0, json_1.parseJsonIfString)(observation.tool_calls[i]);
        const entry = typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
            ? parsed
            : {};
        return {
            id: typeof entry.id === "string" ? entry.id : "",
            name,
            arguments: (0, json_1.parseJsonIfString)(entry.arguments) ?? {},
            type: typeof entry.type === "string" ? entry.type : "",
            index: typeof entry.index === "number" ? entry.index : 0,
        };
    });
}
function convertEventRecordToObservationForEval(record) {
    const metadata = (0, metadata_conversion_1.metadataArraysToRecord)(record.metadata_names, record.metadata_values);
    const experimentItemMetadata = record.experiment_item_metadata_names.length > 0
        ? record.experiment_item_metadata_names.reduce((acc, name, i) => {
            if (!(name in acc)) {
                acc[name] = record.experiment_item_metadata_values[i];
            }
            return acc;
        }, {})
        : undefined;
    const toolCallNames = record.tool_call_names ?? [];
    return exports.observationForEvalSchema.parse({
        ...record,
        metadata,
        experiment_item_metadata: experimentItemMetadata,
        tool_call_count: toolCallNames.length,
    });
}
/**
 * Canonical variable set for code evaluators — one entry per experiment
 * target column below (the id annotation on the column arrays pins them to
 * this list). Web synthesizes rule mappings from it
 * (getCodeEvalVariableMapping) and buildCodeEvalPayload (codeEvalExecution)
 * places each variable in the evaluator payload — see the
 * CODE_EVAL_PAYLOAD_SECTION_BY_VARIABLE tripwire in codeEvalDispatcherTypes.
 */
exports.CODE_EVAL_TEMPLATE_VARIABLES = [
    "input",
    "output",
    "metadata",
    "toolCalls",
    "experimentItemExpectedOutput",
    "experimentItemMetadata",
];
exports.eventTargetEvalVariableColumns = [
    {
        id: "input",
        name: "Input",
        description: "Observation input data",
        internal: "input",
    },
    {
        id: "output",
        name: "Output",
        description: "Observation output data",
        internal: "output",
    },
    {
        id: "metadata",
        name: "Metadata",
        description: "Observation metadata",
        type: "stringObject",
        internal: "metadata",
    },
    {
        id: "toolCalls",
        name: "Tool Calls",
        description: "Tool calls recorded on the observation ({id, name, arguments, type, index})",
        internal: "tool_calls",
    },
];
exports.experimentTargetEvalVariableColumns = [
    ...exports.eventTargetEvalVariableColumns,
    {
        id: "experimentItemExpectedOutput",
        name: "Expected Output",
        description: "Expected output from experiment item",
        internal: "experiment_item_expected_output",
    },
    {
        id: "experimentItemMetadata",
        name: "Experiment Item Metadata",
        description: "Metadata from experiment item",
        type: "stringObject",
        internal: "experiment_item_metadata",
    },
];
/**
 * Columns available for variable extraction in observation-based evals.
 * These are the fields that can be mapped to template variables.
 *
 * When configuring an eval, users can map these columns to template
 * variables like {{input}}, {{output}}, {{expected_output}}, etc.
 */
exports.observationEvalVariableColumns = [
    ...exports.experimentTargetEvalVariableColumns,
];
/**
 * Columns available for filtering in observation-based evals.
 *
 * These columns can be used in filter conditions to determine
 * which observations should be evaluated.
 */
exports.observationEvalFilterColumns = [
    {
        name: "Type",
        id: "type",
        type: "stringOptions",
        internal: "type",
        options: Object.values(domain_1.ObservationType).map((key) => ({ value: key })),
    },
    {
        name: "Name",
        id: "name",
        type: "stringOptions",
        internal: "name",
        options: [], // to be filled at runtime
    },
    {
        name: "Environment",
        id: "environment",
        type: "stringOptions",
        internal: "environment",
        options: [], // to be filled at runtime
    },
    {
        name: "Level",
        id: "level",
        type: "stringOptions",
        internal: "level",
        options: Object.values(domain_1.ObservationLevel).map((key) => ({ value: key })),
    },
    {
        name: "Version",
        id: "version",
        type: "string",
        internal: "version",
        nullable: true,
    },
    {
        name: "Trace Name",
        id: "traceName",
        type: "stringOptions",
        internal: "trace_name",
        options: [], // to be filled at runtime
        nullable: true,
    },
    {
        name: "User ID",
        id: "userId",
        type: "string",
        internal: "user_id",
        nullable: true,
    },
    {
        name: "Session ID",
        id: "sessionId",
        type: "string",
        internal: "session_id",
        nullable: true,
    },
    {
        name: "Tags",
        id: "tags",
        type: "arrayOptions",
        internal: "tags",
        options: [], // to be filled at runtime
    },
    {
        name: "Metadata",
        id: "metadata",
        type: "stringObject",
        internal: "metadata",
    },
    {
        name: "Parent Observation",
        id: "parentObservationId",
        type: "null",
        internal: "parent_span_id",
        nullable: true,
    },
    {
        name: "Called Tool Names",
        id: "calledToolNames",
        type: "arrayOptions",
        internal: "tool_call_names",
        options: [], // to be filled at runtime
    },
    {
        name: "Tool Call Count",
        id: "toolCalls",
        type: "number",
        internal: "tool_call_count",
        step: 1,
        min: 0,
    },
];
exports.experimentEvalFilterColumns = [
    {
        name: "Dataset",
        id: "experimentDatasetId",
        type: "stringOptions",
        internal: "experiment_dataset_id",
        options: [], // to be filled at runtime
    },
];
exports.eventsEvalFilterColumns = [
    ...exports.observationEvalFilterColumns,
    ...exports.experimentEvalFilterColumns,
];
function observationEvalFilterColsWithOptions(options, cols = exports.observationEvalFilterColumns) {
    return cols.map((col) => {
        if (col.id === "environment") {
            return (0, typeHelpers_1.formatColumnOptions)(col, options?.environment ?? []);
        }
        if (col.id === "tags") {
            return (0, typeHelpers_1.formatColumnOptions)(col, options?.tags ?? []);
        }
        if (col.id === "traceName") {
            return (0, typeHelpers_1.formatColumnOptions)(col, options?.traceName ?? []);
        }
        if (col.id === "name") {
            return (0, typeHelpers_1.formatColumnOptions)(col, options?.name ?? []);
        }
        if (col.id === "calledToolNames") {
            return (0, typeHelpers_1.formatColumnOptions)(col, options?.calledToolNames ?? []);
        }
        return col;
    });
}
function experimentEvalFilterColsWithOptions(options, cols = exports.experimentEvalFilterColumns) {
    return cols.map((col) => {
        if (col.id === "experimentDatasetId") {
            return (0, typeHelpers_1.formatColumnOptions)(col, options?.experimentDatasetId ?? []);
        }
        return col;
    });
}
/**
 * Field mapper for observation eval filters.
 * Maps camelCase filter column IDs to snake_case observation fields.
 * Based on events table column definitions.
 *
 * @param observation - The observation data object
 * @param column - The camelCase column ID from filter definitions
 * @returns The value from the observation object
 */
function mapEventEvalFilterColumnIdToField(observation, column) {
    const columnMapping = exports.eventsEvalFilterColumns.find((c) => c.id === column);
    if (!columnMapping) {
        return undefined;
    }
    return observation[columnMapping.internal];
}
